import { useEffect, useState } from "react";
import { BadgePercent, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { errMsg, http } from "@/lib/api";

const DL_RX = /\/api\/documents\/([a-f0-9]{24})\/(?:versions\/\d+\/)?download\?format=(pdf|excel)/i;
const SAVED = "ph_lead_contact";
const field = "mt-1.5 w-full rounded-2xl border border-ph-line bg-ph-bg px-4 py-3 text-sm text-ph-ink outline-none focus:border-ph-accent/60";

const loadSaved = () => {
  try { return JSON.parse(localStorage.getItem(SAVED)) || {}; } catch { return {}; }
};

const startDownload = (href, fmt) => {
  const a = document.createElement("a");
  a.href = href;
  a.setAttribute("download", "");
  a.dataset.leadBypass = "1";
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast.success(`${fmt === "excel" ? "Excel" : "PDF"} download started`);
};

const normalise = (raw) => {
  const p = raw.replace(/[\s\-()]/g, "");
  return /^\d{10}$/.test(p) ? `+91${p}` : p;
};

export function LeadGate() {
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({ phone: "", name: "", city: "", whatsapp: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || a.dataset.leadBypass) return;
      const m = a.getAttribute("href").match(DL_RX);
      if (!m) return;
      e.preventDefault();
      e.stopPropagation();
      setForm({ phone: "", name: "", city: "", whatsapp: false, ...loadSaved() });
      setPending({ href: a.href, docId: m[1], fmt: m[2].toLowerCase() });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const finish = () => { startDownload(pending.href, pending.fmt); setPending(null); };

  const submit = async (e) => {
    e.preventDefault();
    const phone = normalise(form.phone);
    if (!/^\+?\d{7,15}$/.test(phone)) { toast.error("Please enter a valid phone number"); return; }
    setBusy(true);
    try {
      await http.post("/leads", { ...form, phone, doc_id: pending.docId, format: pending.fmt });
      localStorage.setItem(SAVED, JSON.stringify({ ...form, phone: form.phone }));
      toast.success("Thanks! We'll get back to you with the best price.");
      finish();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="lead-dialog">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ph-lime text-ph-navy"><BadgePercent className="h-5 w-5" /></span>
        <DialogTitle className="font-display text-xl font-bold text-ph-ink">Want the best price and discount?</DialogTitle>
        <DialogDescription className="text-sm text-ph-muted">Drop your number and we will get back to you. Your download starts right after.</DialogDescription>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm font-medium text-ph-ink">Phone number
            <input type="tel" inputMode="tel" required autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="98765 43210" maxLength={20} data-testid="lead-phone-input" className={field} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-ph-ink">Name <span className="text-ph-muted">(optional)</span>
              <input value={form.name} onChange={set("name")} maxLength={80} autoComplete="name" data-testid="lead-name-input" className={field} />
            </label>
            <label className="block text-sm font-medium text-ph-ink">City <span className="text-ph-muted">(optional)</span>
              <input value={form.city} onChange={set("city")} maxLength={80} data-testid="lead-city-input" className={field} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-ph-ink">
            <input type="checkbox" checked={form.whatsapp} onChange={set("whatsapp")} data-testid="lead-whatsapp-checkbox" className="h-4 w-4 accent-[#6366F1]" />
            This number is on WhatsApp
          </label>
          <button type="submit" disabled={busy} data-testid="lead-submit-btn" className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-ph-lime text-sm font-semibold text-ph-navy transition-opacity disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Send number &amp; download
          </button>
          <button type="button" onClick={finish} data-testid="lead-skip-btn" className="flex h-11 w-full items-center justify-center rounded-full border border-ph-line text-sm font-medium text-ph-muted transition-colors hover:bg-ph-raised hover:text-ph-ink">
            No thanks, just download
          </button>
          <p className="text-center text-xs text-ph-muted">We only use your number to send you a price quote.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
